import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Purchases as PurchasesNative } from '@revenuecat/purchases-capacitor';
// import { Purchases as PurchasesWeb } from '@revenuecat/purchases-js'; // Only needed if you want to handle success inline, not used for hosted paywall redirect
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, StarIcon, ClockIcon, BookmarkIcon, ShoppingBagIcon } from '@heroicons/react/24/solid'; // Using solid icons for more emphasis
import apiClient from '../utils/apiClient';
import Modal from '../components/Modal'; // TEMPORARY - REMOVE FOR PRODUCTION

export default function SubscribePage() {
  const { isSubscribed, offerings, setIsSubscribed } = useSubscription();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null); // Renamed for clarity
  const userId = localStorage.getItem("userId");
  const [showModal, setShowModal] = useState(false); // TEMPORARY - REMOVE FOR PRODUCTION

  // If already subscribed, redirect to home
  useEffect(() => {
    if (isSubscribed) {
      // alert(isSubscribed); // Consider removing this alert for a smoother UX
      //navigate('/', { replace: true });
    }
  }, [isSubscribed, navigate]);

  // While offerings are loading
  if (offerings === null) {
    return <p className="text-center py-16 text-gray-700 text-lg">Loading delicious plans...</p>;
  }

  const packages = offerings.current.availablePackages || [];

  // Native (Capacitor) purchase flow
  const handlePurchaseNative = async (pkg) => {
    try {
      const result = await PurchasesNative.purchasePackage({ aPackage: pkg });
      const purchaserInfo = result?.customerInfo; // Safely access purchaserInfo

      // Add a robust check for purchaserInfo
      if (!purchaserInfo) {
        console.error('Purchase failed: purchaserInfo is undefined or null.');
        setError('Purchase failed. Please try again.');
        return; // Exit if purchaserInfo is not valid
      }

      // Now, safely access entitlements and active properties
      const isActive = !!purchaserInfo.entitlements?.active?.['sous_premium'];

      if (isActive) {
        // Update backend - upsert so is safe
        apiClient.post('/subscriptions/register', purchaserInfo ).catch((e) => console.error('register failed', e));
        setIsSubscribed(true);
        navigate('/', { replace: true });
      } else {
        // Handle cases where purchase was successful but entitlement is not active
        console.log('Purchase successful, but "sous_premium" entitlement is not active.');
        setError('Purchase completed, but premium access not granted. Please restore purchases or contact support.');
      }
    } catch (e) {
      console.error('Purchase failed', e);
      // More user-friendly error message for common cases
      if (e.code === 'PurchaseCancelled') {
        setError('Purchase cancelled.');
      } else if (e.message) {
        setError(`Purchase failed: ${e.message}`);
      } else {
        setError('Purchase failed due to an unexpected error.');
      }
    }
  };

  // Web redirection to the hosted paywall
  const handleWebRedirect = () => {
    window.location.href = `https://pay.rev.cat/ayynlgknbiqkcuoz/${userId}`;
    // LIVE - https://pay.rev.cat/ayynlgknbiqkcuoz/
    // TEST - https://pay.rev.cat/sandbox/xfipmpqrxuapmezw/
  };

  // Web redirection for free tier
  const handleWebFree = () => {
    navigate('/', { replace: true });
  };

  // Common features for both Pro and Free (if applicable)
  const commonFeatures = [
    { text: 'Get recipes tailored to your tastes, skill level, and kitchen', icon: <CheckCircleIcon className="h-6 w-6 text-green-500" /> },
    { text: 'Chat to edit serving sizes, swap ingredients, or create new dishes', icon: <CheckCircleIcon className="h-6 w-6 text-green-500" /> },
    { text: 'Snap a photo of ingredients and get instant meal ideas', icon: <CheckCircleIcon className="h-6 w-6 text-green-500" /> },
    { text: 'Plan your week with intelligent, waste-saving recipe suggestions', icon: <CheckCircleIcon className="h-6 w-6 text-green-500" /> },
  ];

  // Specific features for each tier
  const freeTierFeatures = [
    { text: '3 recipes per week', icon: <ClockIcon className="h-5 w-5 text-gray-500" /> },
    { text: '10 favourites (lifetime)', icon: <StarIcon className="h-5 w-5 text-gray-500" /> },
    { text: '30 days recipe history', icon: <BookmarkIcon className="h-5 w-5 text-gray-500" /> },
    { text: 'No meal planning smart suggestions', icon: <CheckCircleIcon className="h-5 w-5 text-gray-500" /> },
    { text: "We'll show ads from time to time", icon: <ShoppingBagIcon className="h-5 w-5 text-gray-500" /> },
  ];

  const proTierFeatures = [
    { text: 'Unlimited recipes', icon: <ClockIcon className="h-5 w-5 text-slate-800" /> },
    { text: 'Unlimited favourites', icon: <StarIcon className="h-5 w-5 text-slate-800" /> },
    { text: 'Unlimited recipe history', icon: <BookmarkIcon className="h-5 w-5 text-slate-800" /> },
    { text: 'Planning smart suggestions', icon: <CheckCircleIcon className="h-5 w-5 text-slate-800" /> },
    { text: "No more ads", icon: <ShoppingBagIcon className="h-5 w-5 text-gray-500" /> },
  ];

  // Sort packages to show Monthly first, then Annual
  const sortedPackages = [...packages].sort((a, b) => {
    if (a.packageType === 'MONTHLY' && b.packageType !== 'MONTHLY') return -1;
    if (a.packageType !== 'MONTHLY' && b.packageType === 'MONTHLY') return 1;
    return 0;
  });

  // Native Platform Display
  if (packages.length > 0 && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
    return (
      <div className="relative min-h-[calc(100vh-60px)] bg-gradient-to-br from-gray-50 to-gray-200 overflow-auto py-8">
        <Modal component={SubExplainer} isOpen={showModal} onClose={() => setShowModal(false)} />

        <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg overflow-hidden md:max-w-2xl">
          <div className="p-8">
            <h1 className="text-4xl font-extrabold tracking-tight text-center text-gray-900 mb-6">
              Choose Your Sous Plan
            </h1>
            <p className="text-lg text-gray-600 text-center mb-8">
              Unlock a world of culinary possibilities with Sous.
            </p>

            {/* Feature List */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">What you'll love about Sous:</h2>
              <ul className="space-y-3">
                {commonFeatures.map((feat, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <span className="mr-3">{feat.icon}</span>
                    <span>{feat.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="container mx-auto py-4">
            {/* Pricing Cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mb-4">

              {/* Free Tier Card */}
              <div
                className={`flex flex-col p-6 border-2 rounded-xl transition-all duration-300 ${
                  selectedPackage === 'free' ? 'border-indigo-600 shadow-lg bg-indigo-50' : 'border-gray-200 bg-white'
                }`}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free Tier</h3>
                <p className="text-gray-500 mb-4">Start exploring Sous with basic features.</p>
                <div className="flex-grow">
                  <ul className="space-y-2 text-sm text-gray-700">
                    {freeTierFeatures.map((feat, index) => (
                      <li key={index} className="flex items-center">
                        <span className="mr-2">{feat.icon}</span>
                        <span>{feat.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => setSelectedPackage('free')}
                  className={`mt-6 py-3 rounded-lg font-semibold transition-colors duration-300 ${
                    selectedPackage === 'free'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white text-indigo-600 border border-indigo-600 hover:bg-indigo-50'
                  }`}
                >
                  {selectedPackage === 'free' ? 'Selected' : 'Choose Free'}
                </button>
              </div>

              
              {/* Pro Tier (Monthly/Yearly) */}
              {sortedPackages.map((pkg) => {
                const isMonthly = pkg.packageType === 'MONTHLY';
                const label = isMonthly ? 'Monthly' : 'Annual';
                const price = pkg.product.priceString;
                const discount =
                  !isMonthly &&
                  packages.find((p) => p.packageType === 'MONTHLY')
                    ? Math.round(
                        100 *
                          (1 -
                            pkg.product.price /
                              (packages.find((p) => p.packageType === 'MONTHLY').product.price * 12))
                      )
                    : null;

                return (
                  <div
                    key={pkg.identifier}
                    className={`flex flex-col p-6 border-2 rounded-xl transition-all duration-300 relative ${
                      selectedPackage === pkg.identifier
                        ? 'border-green-600 shadow-lg bg-green-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    {discount && (
                      <span className="absolute top-0 right-0 -mt-3 -mr-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md transform rotate-6">
                        SAVE {discount}%
                      </span>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro {label}</h3>
                    <p className="text-gray-500 mb-4">Unlock all premium features.</p>
                    <div className="text-4xl font-extrabold text-gray-900 mb-4">
                      {price}
                      <span className="text-lg text-gray-500 ml-1 font-medium">{isMonthly ? '/mo' : '/yr'}</span>
                    </div>
                    <div className="flex-grow">
                      <ul className="space-y-2 text-sm text-gray-700">
                        {proTierFeatures.map((feat, index) => (
                          <li key={index} className="flex items-center">
                            <span className="mr-2">{feat.icon}</span>
                            <span>{feat.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      onClick={() => setSelectedPackage(pkg.identifier)}
                      className={`mt-6 py-3 rounded-lg font-semibold transition-colors duration-300 ${
                        selectedPackage === pkg.identifier
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-white text-green-600 border border-green-600 hover:bg-green-50'
                      }`}
                    >
                      {selectedPackage === pkg.identifier ? 'Selected' : 'Choose Pro'}
                    </button>
                  </div>
                );
              })}
              
            </div>
            </div>

            {/* Footer links */}
            <div className="flex justify-center space-x-6 text-sm text-gray-500 mb-8">
              <button onClick={() => PurchasesNative.restorePurchases()} className="underline hover:text-gray-700">
                Restore Purchases
              </button>
              <a href="/terms" className="underline hover:text-gray-700">
                Terms
              </a>
              <a href="/privacy" className="underline hover:text-gray-700">
                Privacy Policy
              </a>
            </div>

            {error && <p className="text-red-500 text-center mt-4 text-sm">{error}</p>}
          </div>
        </div>

           {/* Single Fixed Continue Button */}
    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white shadow-lg z-50"> {/* z-50 ensures it's on top */}
      <button
        onClick={() => {
          if (selectedPackage === 'free') {
            navigate('/');
          } else if (selectedPackage) { // Check if any package (pro or free) is selected
            const pkg = packages.find((p) => p.identifier === selectedPackage);
            handlePurchaseNative(pkg);
          }
        }}
        className={`w-full py-4 rounded-lg font-bold text-white transition-colors duration-300 shadow-md text-lg
          ${
            selectedPackage
              ? selectedPackage === 'free'
                ? 'bg-indigo-600 hover:bg-indigo-700' // Different color for Free selection
                : 'bg-green-600 hover:bg-green-700' // Pro selection
              : 'bg-gray-400 cursor-not-allowed' // Disabled state
          }`
        }
        disabled={!selectedPackage} // Button is disabled if no package is selected
      >
        {selectedPackage === 'free'
          ? 'Continue for Free'
          : selectedPackage
            ? 'Continue with Pro'
            : 'Select an Option to Continue'} {/* Dynamic text */}
      </button>
    </div>
      </div>
    );
  }

  // -----------------------------------------------------
  // WEB: Enhanced Layout for the hosted paywall redirect
  // -----------------------------------------------------
  return (
    <div className="min-h-[calc(100vh-60px)] bg-gradient-to-b from-blue-50 to-indigo-100 flex flex-col items-center px-4 py-12">
      <Modal component={SubExplainer} isOpen={showModal} onClose={() => setShowModal(false)} />

      {/* Hero / Headline */}
      <div className="max-w-2xl text-center mb-12">
        <h1 className="text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
          Unleash Your Inner Chef with <span className="text-sage">Sous Premium</span>
        </h1>
        <p className="text-xl text-gray-700 leading-relaxed">
          Remove ads and get unlimited, personalised recipes, AI-powered meal plans, and
          step-by-step guidance. Elevate your home-cooking.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl w-full mb-12">

        {/* Pro Tier Card (Combined for Web - redirects to paywall) */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 rounded-xl shadow-xl flex flex-col items-center text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-pattern-dots opacity-10"></div> {/* Subtle background pattern */}
          <h2 className="text-3xl font-bold mb-4 z-10">Sous Premium</h2>
          <p className="text-lg mb-6 z-10">Unlock the full power of Sous.</p>
          <ul className="space-y-3 text-left w-full max-w-xs mb-8 z-10">
            {proTierFeatures.map((feat, index) => (
              <li key={index} className="flex items-center">
                <span className="mr-3">{feat.icon}</span>
                <span>{feat.text}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={handleWebRedirect}
            className="w-full py-4 rounded-lg font-bold text-lg bg-white text-green-700 shadow-lg hover:bg-gray-100 transition-colors duration-300 z-10"
          >
            Choose Your Plan & Subscribe
          </button>
          <p className="mt-6 text-sm text-gray-100 z-10">
            Secure checkout powered by Stripe & RevenueCat.
          </p>
        </div>

        {/* Free Tier Card */}
        <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center text-center border-2 border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Free Tier</h2>
          <p className="text-lg text-gray-600 mb-6">Explore Sous with essential features.</p>
          <ul className="space-y-3 text-left w-full max-w-xs text-gray-700 mb-8">
            {freeTierFeatures.map((feat, index) => (
              <li key={index} className="flex items-center">
                <span className="mr-3">{feat.icon}</span>
                <span>{feat.text}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={handleWebFree}
            className="w-full py-4 rounded-lg font-bold text-lg border border-sage text-sage shadow-lg hover:bg-sage hover:text-white transition-colors duration-300"
          >
            Continue for Free
          </button>
        </div>

      </div>

      {/* Additional reassurance / FAQs */}
      <div className="max-w-2xl text-center text-sm text-gray-600 mb-6">
        <p>
          No commitments. Cancel anytime. Your card will only be charged once
          you confirm a plan on the next page.
        </p>
      </div>

      {/* Footer links */}
      <div className="flex space-x-6 text-xs text-gray-500">
        <a href="/terms" className="underline hover:text-gray-700">
          Terms
        </a>
        <a href="/privacy" className="underline hover:text-gray-700">
          Privacy Policy
        </a>
      </div>

      {error && <p className="text-red-500 text-center mt-4 text-sm">{error}</p>}
    </div>
  );

  // TEMPORARY - REMOVE FOR PRODUCTION
  function SubExplainer() {
    return (
      <div className="text-gray-800">
        <h3 className="text-xl font-bold mb-3">Sous is prepping for app store launch!</h3>
        <p className="mb-4">
          You can still use Sous as normal. While the app is in this live trial,
          subscriptions are active but using **test cards**, so you **will not** be charged.
        </p>
        <h4 className="text-lg font-semibold mb-2">Test Card Credentials:</h4>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>**Card No.** 4242 4242 4242 4242</li>
          <li>**Expiry Date:** Any future date</li>
          <li>**Postcode:** Any valid UK postcode, e.g., WS111DB</li>
        </ul>
        <p className="text-sm text-gray-600">
          Note: These subscriptions expire quickly as they are in trial mode (Monthly = 5 mins, Yearly = 30 mins). They auto-renew 6 times then cancel.
        </p>
      </div>
    );
  }
}